import React from 'react';
import { LocaleImage } from '@/i18n/router';
import { cn } from '@/lib/utils';
import { hardchargerArt } from '@/data/hardcharger';

interface HardchargerArtProps {
  layout?: 'stacked' | 'horizontal';
  /** Which art to show: 'theme' follows the site theme (light art with navy lettering on the light theme,
   *  dark art with white lettering on the dark one); 'dark' always shows the white-lettered art (for
   *  surfaces that are dark in both themes). */
  variant?: 'theme' | 'dark';
  /** The LCP image of the page: load the dark art eagerly with high priority. The site is dark-first, so
   *  the light art stays lazy; hidden (display: none) lazy images are never fetched, so a dark-theme
   *  visitor downloads one image. */
  priority?: boolean;
  sizes: string;
  className?: string;
  alt?: string;
}

const HardchargerArt = ({ layout = 'stacked', variant = 'theme', priority = false, sizes, className, alt = 'Hardcharger™: the charging bull and wordmark' }: HardchargerArtProps) => {
  const art = hardchargerArt[layout];
  const common = { width: art.width, height: art.height, sizes, alt, decoding: 'async' as const };
  // React 18 drops camelCase fetchPriority, so pass the lowercase attribute.
  const eager = priority ? { loading: 'eager' as const, ...{ fetchpriority: 'high' } } : { loading: 'lazy' as const };
  if (variant === 'dark') {
    return <LocaleImage src={art.dark.src} srcSet={art.dark.srcSet} {...common} {...eager} className={cn('h-auto w-full', className)} />;
  }
  return (
    <>
      <LocaleImage src={art.dark.src} srcSet={art.dark.srcSet} {...common} {...eager} className={cn('hidden h-auto w-full dark:block', className)} />
      <LocaleImage src={art.light.src} srcSet={art.light.srcSet} {...common} loading="lazy" className={cn('block h-auto w-full dark:hidden', className)} />
    </>
  );
};

export default HardchargerArt;
